import { useState, useEffect, useCallback, useRef } from "react";
import { accountApi } from "../services/api";
import authService from "../services/auth";
import logger from "../utils/logger";

const CACHE_KEY = "userCreditsCache";
const CACHE_TIME_KEY = "userCreditsCacheTime";
const CACHE_TTL_MS = 60 * 1000; // 1 minute (shorter than plan cache — credits change frequently)
const POLL_INTERVAL_MS = 60 * 1000; // 60 seconds

/**
 * Custom hook for fetching and polling credit balances.
 *
 * Fetches GET /api/account/credits on mount, caches in sessionStorage,
 * and polls at a 60-second interval. Provides a manual refresh function
 * for immediate re-fetch after known mutations (e.g. sending a message).
 *
 * @returns {Object}
 *   @returns {{ haiku: number, sonnet: number, opus: number }|null} credits
 *   @returns {{ haiku: number|null, sonnet: number|null, opus: number|null }|null} thresholds
 *   @returns {string[]} lowBalanceWarnings - model families below their threshold
 *   @returns {{ haiku: number, sonnet: number, opus: number }|null} usageThisMonth - credits used this calendar month
 *   @returns {boolean} loading - true during initial fetch (not during polls)
 *   @returns {string|null} error
 *   @returns {() => void} refresh - manually trigger a re-fetch
 */
export function useCredits() {
  const [credits, setCredits] = useState(null);
  const [thresholds, setThresholds] = useState(null);
  const [lowBalanceWarnings, setLowBalanceWarnings] = useState([]);
  const [usageThisMonth, setUsageThisMonth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const controllerRef = useRef(null);
  const pollControllerRef = useRef(null);
  const mountedRef = useRef(true);

  const fetchCredits = useCallback(async (signal, isInitial = false) => {
    try {
      // Check sessionStorage cache on initial load only
      if (isInitial) {
        const cached = sessionStorage.getItem(CACHE_KEY);
        const cachedTime = sessionStorage.getItem(CACHE_TIME_KEY);
        const now = Date.now();
        const isCacheValid =
          cached &&
          cachedTime &&
          now - Number.parseInt(cachedTime, 10) < CACHE_TTL_MS;

        if (isCacheValid) {
          const parsed = JSON.parse(cached);
          if (!mountedRef.current) return;
          setCredits(parsed.credits);
          setThresholds(parsed.thresholds);
          setLowBalanceWarnings(parsed.lowBalanceWarnings || []);
          setUsageThisMonth(parsed.usageThisMonth || null);
          setLoading(false);
          return;
        }
      }

      if (isInitial) {
        setLoading(true);
        setError(null);
      }

      const response = await accountApi.getCredits({ signal });
      if (!mountedRef.current) return;

      const data = {
        credits: response.credits ?? null,
        thresholds: response.thresholds ?? null,
        lowBalanceWarnings: response.lowBalanceWarnings ?? [],
        usageThisMonth: response.usageThisMonth ?? null,
      };

      setCredits(data.credits);
      setThresholds(data.thresholds);
      setLowBalanceWarnings(data.lowBalanceWarnings);
      setUsageThisMonth(data.usageThisMonth);
      setError(null);

      // Update cache
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(data));
      sessionStorage.setItem(CACHE_TIME_KEY, String(Date.now()));
    } catch (err) {
      if (err.name === "AbortError" || err.code === "ERR_CANCELED") return;
      if (!mountedRef.current) return;
      logger.warn("Failed to fetch credits", { error: err.message });
      if (isInitial) {
        setError("Could not load credit balances.");
      }
      // On poll failure, keep stale data visible — don't clear credits
    } finally {
      if (isInitial && mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // Track mounted state to prevent setState after unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Initial fetch (skipped if not authenticated)
  useEffect(() => {
    if (!authService.isAuthenticated()) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    controllerRef.current = controller;
    fetchCredits(controller.signal, true);
    return () => controller.abort();
  }, [fetchCredits]);

  // Polling — pauses when tab is hidden to save network requests
  useEffect(() => {
    if (!authService.isAuthenticated()) return;

    let interval = null;

    const startPolling = () => {
      if (interval) return;
      interval = setInterval(() => {
        if (!authService.isAuthenticated()) return;
        // Audit #14: separate ref from controllerRef to avoid clobbering refresh()
        if (pollControllerRef.current) pollControllerRef.current.abort();
        const controller = new AbortController();
        pollControllerRef.current = controller;
        fetchCredits(controller.signal, false);
      }, POLL_INTERVAL_MS);
    };

    const stopPolling = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
      if (pollControllerRef.current) {
        pollControllerRef.current.abort();
        pollControllerRef.current = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // Audit #14: separate ref to avoid clobbering refresh()
        if (pollControllerRef.current) pollControllerRef.current.abort();
        const controller = new AbortController();
        pollControllerRef.current = controller;
        fetchCredits(controller.signal, false);
        startPolling();
      } else {
        stopPolling();
      }
    };

    // Start polling only if tab is currently visible
    if (document.visibilityState === "visible") {
      startPolling();
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (controllerRef.current) controllerRef.current.abort();
      if (pollControllerRef.current) pollControllerRef.current.abort();
    };
  }, [fetchCredits]);

  // Manual refresh (invalidates cache, fetches immediately)
  // TODO: consider adding optimistic deduct(family, amount) for post-message UX
  const refresh = useCallback(() => {
    sessionStorage.removeItem(CACHE_KEY);
    sessionStorage.removeItem(CACHE_TIME_KEY);
    // Abort any in-flight request before starting a new one
    if (controllerRef.current) {
      controllerRef.current.abort();
    }
    const controller = new AbortController();
    controllerRef.current = controller;
    fetchCredits(controller.signal, false);
  }, [fetchCredits]);

  return {
    credits,
    thresholds,
    lowBalanceWarnings,
    usageThisMonth,
    loading,
    error,
    refresh,
  };
}

export default useCredits;
